<template>
  <div class="flex items-center gap-2 min-w-fit">
    <div class="flex bg-white items-center px-2 rounded-lg text-xs mx-1 min-w-fit">
      {{ $t("reports.filters.from") }}
      <DatePicker
        format="yyyy-MM-dd HH:mm"
        position="center"
        auto-apply
        :close-on-auto-apply="false"
        v-model="fromDate"
        :rules="dateRule.concat(startDateRule)"
        :max-date="maxFromDate || toDate"
        placeholder="from"
        text-input
        required
        :clearable="false"
        @update:model-value="handleFromDateChange"
      />
    </div>
    <div class="flex bg-white items-center px-2 rounded-lg text-xs mx-1 min-w-fit">
      {{ $t("reports.filters.to") }}
      <DatePicker
        v-model="toDate"
        auto-apply
        :close-on-auto-apply="false"
        :rules="dateRule.concat(endDateRule)"
        format="yyyy-MM-dd HH:mm"
        :min-date="minToDate || fromDate"
        placeholder="to"
        text-input
        required
        :clearable="false"
        @update:model-value="handleToDateChange"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'FilterDateRange',
  props: {
    from: {
      type: String,
      default: () => moment().startOf("day").format("YYYY-MM-DD HH:mm:ss")
    },
    to: {
      type: String,
      default: () => moment().endOf("day").format("YYYY-MM-DD HH:mm:ss")
    }
  },
  emits: ['update:from', 'update:to', 'change'],
  data() {
    return {
      fromDate: this.convertToDate(this.from),
      toDate: this.convertToDate(this.to),
      dateRule: [
        (v) => (!!v && moment(v, "YYYY-MM-DD HH:mm:ss", true).isValid()) || this.$t("reports.filters.invalid_date"),
        (v) => !!v || this.$t("reports.filters.date_missing"),
      ]
    };
  },
  computed: {
    endDateRule() {
      return [
        () => moment(this.toDate).isAfter(this.fromDate) || this.$t("reports.filters.end_bigger_than_start"),
        () => {
          if (!this.fromDate || !this.toDate) return true;
          const fromYear = moment(this.fromDate).year();
          const toYear = moment(this.toDate).year();
          return fromYear === toYear || this.$t("reports.filters.same_year_required") || "Dates must be in the same year";
        }
      ];
    },
    startDateRule() {
      return [
        () => moment(this.fromDate).isBefore(this.toDate) || this.$t("reports.filters.start_less_than_end"),
        () => {
          if (!this.fromDate || !this.toDate) return true;
          const fromYear = moment(this.fromDate).year();
          const toYear = moment(this.toDate).year();
          return fromYear === toYear || this.$t("reports.filters.same_year_required") || "Dates must be in the same year";
        }
      ];
    },
    // Max date for "from" picker: end of the year of "to" date
    maxFromDate() {
      if (!this.toDate) return null;
      // If "to" is set, limit "from" to the end of that year
      const toYearEnd = moment(this.toDate).endOf('year').toDate();
      // Also ensure it's not after toDate
      return this.toDate < toYearEnd ? this.toDate : toYearEnd;
    },
    // Min date for "to" picker: start of the year of "from" date
    minToDate() {
      if (!this.fromDate) return null;
      // If "from" is set, limit "to" to the start of that year
      const fromYearStart = moment(this.fromDate).startOf('year').toDate();
      // Also ensure it's not before fromDate
      return this.fromDate > fromYearStart ? this.fromDate : fromYearStart;
    },
    isValid() {
      if (!this.fromDate || !this.toDate) return false;
      const fromMoment = moment(this.fromDate);
      const toMoment = moment(this.toDate);
      return fromMoment.isValid() &&
             toMoment.isValid() &&
             toMoment.isAfter(fromMoment) &&
             fromMoment.year() === toMoment.year();
    }
  },
  methods: {
    convertToDate(value) {
      // Convert string to Date for DatePicker component
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value === 'string') {
        const parsed = moment(value, "YYYY-MM-DD HH:mm:ss", true);
        return parsed.isValid() ? parsed.toDate() : null;
      }
      return null;
    },
    convertToString(value) {
      // Convert Date to string for parent component
      if (!value) return '';
      if (value instanceof Date) {
        return moment(value).format("YYYY-MM-DD HH:mm:ss");
      }
      if (typeof value === 'string') {
        return value;
      }
      return '';
    },
    handleFromDateChange(value) {
      this.fromDate = value;
      const stringValue = this.convertToString(value);
      this.$emit('update:from', stringValue);
      this.$emit('change', { from: stringValue, to: this.convertToString(this.toDate) });
    },
    handleToDateChange(value) {
      this.toDate = value;
      const stringValue = this.convertToString(value);
      this.$emit('update:to', stringValue);
      this.$emit('change', { from: this.convertToString(this.fromDate), to: stringValue });
    },
    getFormattedDates() {
      return {
        from: moment(this.fromDate, "YYYY-MM-DD HH:mm:ss", true).format("YYYY-MM-DD HH:mm:ss"),
        to: moment(this.toDate, "YYYY-MM-DD HH:mm:ss", true).format("YYYY-MM-DD HH:mm:ss")
      };
    }
  },
  watch: {
    from(newValue) {
      this.fromDate = this.convertToDate(newValue);
    },
    to(newValue) {
      this.toDate = this.convertToDate(newValue);
    }
  }
};
</script> 